import type { ReactNode } from "react";
import { cn } from "@/lib/utils/cn";
import { Breadcrumbs, type BreadcrumbCrumb } from "./Breadcrumbs";

export interface PageHeroProps {
  /** Page title — sentence case. Maps to `display-1` font. */
  title: string;
  /** Optional subtitle / context line. */
  subtitle?: string;
  /**
   * Optional eyebrow label rendered ABOVE the title (small text-meta caps
   * per `mockup/mobile.html` §1, §3, §4, §5, §6, §10).  Examples:
   * `一起的回憶` (Albums), `相簿 · 三週年` (AlbumDetail),
   * `WELCOME BACK` (Dashboard), etc.
   *
   * Added per `wiki/w2.3-non-spec-audit.md` L1 — was previously rendered
   * as a separate `<div>` workaround in Albums + AlbumDetail routes.
   *
   * Per W35.5 spec §2.1: a plain string is the common case, but detail
   * pages (e.g. DiaryDetail / AnniversaryDetail) need to render a
   * "back-to-list" link as the eyebrow — that's a `ReactNode`, not a
   * literal string. The type widens to `ReactNode` so the primitive
   * stays single-source-of-truth across list + detail pages.
   */
  eyebrow?: ReactNode;
  /**
   * W45.0 `ADR-0115` — the two-level breadcrumb, which **replaces** the eyebrow
   * back-link that six detail pages each implemented differently.
   *
   * It lives here rather than in the topbar because F28 fixes the topbar at
   * exactly three components (wordmark · bell · avatar) precisely so it stays the
   * one invariant line across all 44 routes; a route-scoped trail would break
   * that. Passing both is possible and wrong — `crumbs` wins, and the eyebrow
   * drops, because two competing "you are here" lines is the redundancy
   * `ADR-0109` removed elsewhere.
   */
  crumbs?: readonly BreadcrumbCrumb[];
  /** Optional filter chips slot (replaces deprecated FilterBar). */
  chips?: ReactNode;
  /** Optional right-aligned action (e.g. settings cog). */
  trailing?: ReactNode;
  className?: string;
}

/**
 * P1 — PageHero.
 *
 * Per `component-api.md §2 P1`:
 *   - 60px glass-2 header with `display-1` sentence-case title
 *   - Optional subtitle + filter chips slot
 *   - Use `<PageHero subtitle="...">` in place of deprecated `FilterBar`
 *
 * Glass-2 backdrop + dawn/dusk tint slot is reserved for Phase 2
 * (per `DESIGN.md §1.4`).
 */
export function PageHero({
  title,
  subtitle,
  eyebrow,
  crumbs,
  chips,
  trailing,
  className,
}: PageHeroProps) {
  // A trail and an eyebrow are two answers to the same question, so the trail
  // wins outright rather than both rendering.
  const showEyebrow = Boolean(eyebrow) && !crumbs;
  return (
    <header
      data-component="PageHero"
      data-eyebrow={showEyebrow ? "true" : undefined}
      data-crumbs={crumbs ? String(crumbs.length) : undefined}
      data-subtitle={subtitle ? "true" : undefined}
      className={cn(
        // `min-h-16` (was `h-16`) so the hero grows to fit content when
        // eyebrow + title + subtitle are all stacked (per W32 polish: diary
        // /diaries now ships all three and a fixed 64px container was clipping
        // the subtitle + hairline-b into the eyebrow row). Anniversary
        // (`eyebrow + title`, no subtitle) still renders at the 64px baseline.
        // W49.0 §6. This comment used to say 「PageContainer owns the gutter;
        // PageHero stays inside the page gutter without negative-margin bleed」
        // — and it is the **reason** five wishlist pages wrapped the hero in
        // `PageContainer`, which adds its own identical `px-s-4 md:px-s-6` plus
        // `py-s-5`. Every wishlist title therefore sat 16px further right and
        // 20px lower than the other thirty pages. The hero carries its own
        // horizontal padding on the second class line below; it is a **sibling**
        // of `PageContainer`, never a child.
        //
        // **W42 F18 — the hero lost its background AND its `hairline-b`.** It had
        // `bg-paper/92 backdrop-blur-xl` plus a bottom hairline, which put a hard
        // edge across the screen: all six `w42-capture-*.png` shots showed a
        // visible boundary at `y≈88`, and wishes showed a second rule under the
        // subtitle at `y≈320`. The reference AppShell is three rows on **one**
        // page ground — no topbar fill, no rule above or below the title.
        //
        // The 0.92 alpha had a real reason to exist (W41: over a pink corner it
        // left `ink-1` short of contrast) and F29 resolves that from the other
        // end — the wash is now paler, so the title clears AA on the page ground
        // directly. `docs/audit/contrast-matrix.mjs` holds the number.
        //
        // **W45.0 `ADR-0111` — `sticky top-0 z-10` is GONE, and this is the
        // actual fix.** `MobileHeader` was `sticky top-0 z-20` at the same time,
        // so two rows were competing for the same 44px band and this one lost on
        // z-index and slid underneath: 「首頁」 over the avatar and the bell. Only
        // the topbar is sticky now, and it owns the material
        // (`.shell-bar::after` in `globals.css`).
        //
        // The title is **content**, not chrome. It scrolls with the page and
        // leaves no residue.
        "min-h-16 py-2",
        "flex items-center gap-3 px-s-4 md:px-s-6",
        className,
      )}
    >
      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        {crumbs ? (
          // The trail replaces the eyebrow. It also replaces the back button, so
          // the pages that carried a `ChevronLeft` in the eyebrow slot drop it
          // rather than keeping both.
          <Breadcrumbs crumbs={crumbs} className="-ml-1 -mt-1 mb-0.5" />
        ) : eyebrow ? (
          <p
            data-testid="page-hero-eyebrow"
            className="text-meta font-semibold uppercase tracking-wider text-ink-3 truncate"
          >
            {eyebrow}
          </p>
        ) : null}
        {/* `line-clamp-2`, not `truncate`. The hero title is the one line on the
            screen that says *which* record you are looking at, and a 4-viewport
            sweep measured it losing more than half its own text: 「週末去看展覽」
            is 199px at `display-1` and had 106px of room at 360px wide, so three
            of its six characters were replaced by an ellipsis (W46.0).

            Clamping at two lines keeps the single-line case — which is almost
            every page, and every screenshot in `docs/audit/` — pixel-identical,
            and only spends height on the long titles that were previously being
            cut. `leading-none` is retained so the one-line baseline does not
            move; a rare second line is tight, which is the right trade against
            losing the name of the diary.

            The eyebrow and subtitle below keep `truncate`: they are `text-meta`
            secondary metadata, where a single-line ellipsis is the convention and
            the content is still identifiable from its first characters. */}
        <h1 className="font-display text-display-1 text-ink-1 line-clamp-2 text-balance tracking-tight leading-none">
          {title}
        </h1>
        {subtitle ? (
          // `line-clamp-2`, for the same reason as the title above: a 4-viewport
          // sweep found this cutting whole clauses off two **sentences**. On
          // `/settings/pair/dissolve` the subtitle is 「解除後你們會各自回到未配對狀態,
          // 之前的日記和紀念日會保留在對方的空間裡。」 and 28% of it — the entire
          // reassurance about what happens to your diaries — was replaced by an
          // ellipsis, on the one screen where a user is about to destroy a
          // relationship's shared history. `/wishes/new` lost 21% the same way.
          //
          // The eyebrow above keeps `truncate`: it is a short uppercase label
          // (`建立`, `解除`), never a sentence, and its first characters already
          // identify the screen.
          <p className="text-body-sm text-ink-3 line-clamp-2 text-balance mt-0.5">{subtitle}</p>
        ) : null}
        {chips ? <div className="mt-2 flex gap-2">{chips}</div> : null}
      </div>
      {trailing ? <div className="shrink-0">{trailing}</div> : null}
    </header>
  );
}
